import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { Pvz } from 'sections/LKSection/modules/PvzModule/stores/entity/Pvz'
import { PickPointFileType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Margin } from 'components/ui'
import { Href } from 'components/ui/Href/Href'
import { Info } from 'components/ui/Info/Info'
import { Divider, Flex, FormItem, Input, Paragraph, Text, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { WEB_LINK_REGEXP } from 'shared/constants/regExp'

import { PickPointFilesViewModel } from '../../models/PickPointFilesViewModel'
import { PickPointFileItem } from './PickPointFileItem/PickPointFileItem'
import {
  baseBtiDocumentData,
  baseLeaseDocumentData,
  createOrderPhotoItems,
  pickPointPhotoItems,
  pickPointVideoItem,
} from './data'

type PickPointFilesFormProps = {
  uiStore: PickPointFilesViewModel
  entity?: Order | Pvz | null
  initialized?: boolean
  isCreation?: boolean
  isActivation?: boolean
  disabled?: boolean
}

export const PickPointFilesForm = observer(
  ({
    uiStore,
    entity,
    initialized = false,
    isCreation = false,
    isActivation = false,
    disabled = false,
  }: PickPointFilesFormProps) => {
    const { t } = useTranslation()
    const [form] = Form.useForm()

    const photoItems = isActivation ? pickPointPhotoItems : createOrderPhotoItems

    useEffect(() => {
      form.setFieldValue('url', uiStore.videoURL)
    }, [uiStore.videoURL])

    const getKey = (key: string) => `${OrderKeys.filesStep}.${key}`

    return (
      <Form form={form} layout="vertical">
        {(isCreation || isActivation) && !disabled && (
          <>
            <Info>
              <Text>{t(getKey('В процессе заполнения'))}</Text>
              <Text>{t(getKey('Обратите внимание'))}</Text>
            </Info>

            <Paragraph>{t(getKey('Фотографии нужны'))}</Paragraph>
            <Paragraph>{t(getKey('Подойдите к этому этапу'))}</Paragraph>
          </>
        )}

        <Margin mb={16}>
          <Title level={5}>{t(getKey('Фотографии ПВЗ'))}</Title>
        </Margin>

        {photoItems.map((photoItem) => (
          <FormItem key={photoItem.fileType}>
            <PickPointFileItem
              uiStore={uiStore}
              entity={entity}
              isCreation={isCreation}
              disabled={disabled}
              fileType={photoItem.fileType}
              label={photoItem.label}
              description={photoItem.description}
              dataTestId={photoItem.dataTestId}
              initialized={initialized}
            />
          </FormItem>
        ))}

        <Margin mb={16}>
          <Title level={5}>{t(getKey('Видеопрогулка'))}</Title>
        </Margin>

        {isCreation && (
          <>
            <Paragraph>{t(getKey('Видеообзор должен начинаться'))}</Paragraph>
            <Paragraph>{t(getKey('На видео должна быть'))}</Paragraph>
            <Paragraph>{t(getKey('На видеообзоре должно быть'))}</Paragraph>
            <Paragraph>{t(getKey('Видеообзор должен быть'))}</Paragraph>
            <Paragraph>{t(getKey('Напоминаем необходимо'))}</Paragraph>
            <Paragraph>{t(getKey('Если возникли проблемы с созданием'))}</Paragraph>
          </>
        )}

        {isActivation && (
          <>
            <Paragraph>{t(getKey('Для подтверждения брендирования'))}</Paragraph>
            <Paragraph>
              <Flex vertical>
                <Text>{t(getKey('адресную табличку'))}</Text>
                <Text>{t(getKey('панорамный вид'))}</Text>
                <Text>{t(getKey('фасад с вывеской'))}</Text>
                <Text>{t(getKey('клиентскую и складскую зоны'))}</Text>
              </Flex>
            </Paragraph>
            <Paragraph>{t(getKey('Начните с улицы'))}</Paragraph>
            <Paragraph>{t(getKey('Продолжительность видео'))}</Paragraph>
            <Paragraph>{t(getKey('Если возникли проблемы с созданием'))}</Paragraph>
          </>
        )}

        {!disabled ? (
          <>
            <FormItem>
              <PickPointFileItem
                uiStore={uiStore}
                entity={entity}
                fileType={PickPointFileType.video}
                label={pickPointVideoItem.label}
                description={pickPointVideoItem.description}
                dataTestId={pickPointVideoItem.dataTestId}
              />
            </FormItem>

            <Form.Item<{ url: string }>
              name="url"
              label={t(getKey('videoURLLabel'))}
              tooltip={t(getKey('videoURLTooltip'))}
              rules={[{ pattern: WEB_LINK_REGEXP, message: t(getSharedKey('URLIncorrectMessage')) }]}
            >
              <Input
                value={uiStore.videoURL}
                onChange={(e) => uiStore.setVideoURL(e.target.value)}
                placeholder="https://"
                data-testid="orderVideoURL"
              />
            </Form.Item>
          </>
        ) : (
          <Href href={uiStore.videoURL} linkType="search" target="_blank">
            {t(getKey('Ссылка на видеопрогулку'))}
          </Href>
        )}

        <Divider />

        <Margin mb={16}>
          <Title level={5}>{t(getKey('Фото или сканы документов'))}</Title>
        </Margin>

        {(isCreation || isActivation) && (
          <>
            <Paragraph>{t(getKey('Приложите фото или отсканированные'))}</Paragraph>
            <Paragraph>{t(getKey('Поддерживаемые форматы'))}</Paragraph>
          </>
        )}

        {isActivation && (
          <FormItem key={PickPointFileType.leaseDocument}>
            <PickPointFileItem
              uiStore={uiStore}
              entity={entity}
              disabled={disabled}
              isCreation={isCreation}
              fileType={PickPointFileType.leaseDocument}
              label={baseLeaseDocumentData.label}
              description={baseLeaseDocumentData.description}
              dataTestId={baseLeaseDocumentData.dataTestId}
              initialized={initialized}
            />
          </FormItem>
        )}

        <FormItem key={PickPointFileType.btiDocument}>
          <PickPointFileItem
            uiStore={uiStore}
            entity={entity}
            disabled={disabled}
            isCreation={isCreation}
            fileType={PickPointFileType.btiDocument}
            label={baseBtiDocumentData.label}
            description={baseBtiDocumentData.description}
            dataTestId={baseBtiDocumentData.dataTestId}
            initialized={initialized}
          />
        </FormItem>
      </Form>
    )
  },
)
